import _extends from '@babel/runtime/helpers/extends';
import React__default, { forwardRef, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { C as CloseButton } from '../Button/CloseButton/CloseButton-be529651.js';
import { D as DrawerSurface } from './DrawerSurface-cfc9ae51.js';
import { c as composeRefs } from '../LocalModules/storm-ui-utils/dist/esm/composeRefs-cac293ab-4034111b.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-39f30e88.js';
import { i as isAndroidFirefox$1 } from '../LocalModules/storm-ui-utils/dist/esm/isAndroidFirefox-f5997692-ba631597.js';
import { e as elementRevealsVirtualKeyboardOnFocus$1 } from '../LocalModules/storm-ui-utils/dist/esm/virtualKeyboard/elementRevealsVirtualKeyboardOnFocus-72bd96fa-a02066f2.js';
import { w as withTaktFallbackId } from '../TaktIdContext/TaktIdContext-f5c69fbd.js';

/**
 * Position the close button and ensure RTL is handled.
 */
const PositionedCloseButton = /*#__PURE__*/styled(CloseButton).withConfig({
  componentId: "sc-storm-ui-30045097__sc-1hcm1dl-0"
})(["position:absolute;left:auto;inset-inline-start:auto;right:", ";inset-inline-end:", ";[dir=\"rtl\"] &&{right:auto;inset-inline-start:auto;left:", ";inset-inline-end:", ";}"], ({
  theme
}) => theme.spacing.base, ({
  theme
}) => theme.spacing.base, ({
  theme
}) => theme.spacing.base, ({
  theme
}) => theme.spacing.base);
const androidFirefoxBodyOverflowHiddenGap = 56;
function createSetBottomToVisualViewport(divRef) {
  const isAndroidFirefox = isAndroidFirefox$1();
  return function setBottomToVisualViewport() {
    if (divRef.current instanceof HTMLElement && window.visualViewport) {
      const {
        height: viewportHeight
      } = window.visualViewport;
      const {
        outerHeight,
        innerHeight
      } = window;
      const divStyle = divRef.current.style;
      if (isAndroidFirefox) {
        const isVirtualKeyboard = elementRevealsVirtualKeyboardOnFocus$1(document.activeElement);
        if (isVirtualKeyboard) {
          divStyle.setProperty('bottom', `${viewportHeight - innerHeight + androidFirefoxBodyOverflowHiddenGap}px`);
          return;
        }
        if (viewportHeight > outerHeight && !isVirtualKeyboard) {
          divStyle.setProperty('bottom', `${androidFirefoxBodyOverflowHiddenGap}px`);
          return;
        }
      }
      if (innerHeight > viewportHeight) {
        divStyle.setProperty('bottom', `${innerHeight - viewportHeight}px`);
        return;
      }
      divStyle.removeProperty('bottom');
    }
  };
}
const DrawerCloseButton = withTaktFallbackId(PositionedCloseButton);
const Overlay = /*#__PURE__*/forwardRef(({
  slideState,
  withCloseButton = true,
  closeButtonLabel = '',
  handleCloseButtonClicked,
  closeButtonProps = {},
  children,
  drawerContainerRef,
  ...rest
}, ref) => {
  const containerRef = useRef(null);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    /**
     * Work-around for Android Firefox bug which causes 56px of whitespace
     * at the bottom of the screen when the ContextualDrawer is opened. On resize, if
     * the visualViewport height is greater than the outerHeight of the window, we
     * assume the whitespace is visible and add 56px to the bottom of the BottomSheet
     * to account for the whitespace blocking any content. Will remove once the
     * browser bug is fixed.
     */
    if (typeof window !== 'undefined' && 'visualViewport' in window && window.visualViewport) {
      const {
        visualViewport
      } = window;
      const setBottomToVisualViewport = createSetBottomToVisualViewport(containerRef);
      visualViewport.addEventListener('resize', setBottomToVisualViewport);
      setBottomToVisualViewport();
      return () => {
        visualViewport.removeEventListener('resize', setBottomToVisualViewport);
      };
    }
  }, [containerRef]);
  return /*#__PURE__*/React__default.createElement(DrawerSurface, _extends({}, rest, {
    ref: composeRefs(containerRef, ref, drawerContainerRef),
    slideState: slideState,
    withCloseButton: withCloseButton
  }), withCloseButton && /*#__PURE__*/React__default.createElement(DrawerCloseButton, _extends({
    size: "lg",
    label: closeButtonLabel,
    onClick: handleCloseButtonClicked,
    taktFallbackId: createStormTaktId('drawer-close-button')
  }, closeButtonProps)), children);
});
Overlay.displayName = 'Overlay';
Overlay.defaultProps = {
  closeButtonLabel: '',
  closeButtonProps: undefined,
  withCloseButton: true
};

export { Overlay as O };
//# sourceMappingURL=Overlay-9b252c23.js.map
